import _ from "lodash";

import { formDataToStructuredObject } from "utils/formDataUtils";
import { PaymentSourceInputName } from "v2/dataroutes/BillingData";


type PaymentSourceBase = {
  id: string;
  isPrimary: boolean;
};

export type CardPaymentSource = PaymentSourceBase & {
  type: "card";
  brand: string;
  last4: string;
  expiry: {
    month: number;
    year: number;
  };
  nameOnCard?: string;
};

export type PayPalPaymentSource = PaymentSourceBase & {
  type: "paypal";
  paypalEmail: string | undefined;
};


export type PaymentSource = CardPaymentSource | PayPalPaymentSource;


type BackendPaymentSourceBase = {
  id: string;
  is_primary: boolean;
};

type BackendCardPaymentSource = BackendPaymentSourceBase & {
  type: "card";
  card: {
    brand: string;
    last4: string;
    expiry_month: number;
    expiry_year: number;
    first_name?: string;
  }
};

type BackendPayPalPaymentSource = BackendPaymentSourceBase & {
  type: "paypal" | "paypal_express_checkout";
  paypal?: {
    email?: string;
  };
};

export type BackendPaymentSource = BackendCardPaymentSource | BackendPayPalPaymentSource;


type PaymentSourceRequestBase = {
  id: string | undefined;
};

type PayPalPaymentSourceRequest = PaymentSourceRequestBase & {
  type: "paypal";
  billing_agreement_token: string;
};

type CardPaymentSourceRequest = PaymentSourceRequestBase & {
  type: "card";
  number?: string;
  cvv?: string;
  expiry: {
    year: string;
    fullYear: string;
    month: string;
  };
  name_on_card?: string;
};

export type PaymentSourceRequest = CardPaymentSourceRequest | PayPalPaymentSourceRequest;


export function fromBackendPaymentSource(source: BackendPaymentSource): PaymentSource {
  if (source.type === "card") {
    return fromBackendCardPaymentSource(source);
  } else if (source.type === "paypal" || source.type === "paypal_express_checkout") {
    return fromBackendPayPalPaymentSource(source);
  }

  throw new Error("Unsupported payment source type: " + (source as any).type);
}

function fromBackendCardPaymentSource(source: BackendCardPaymentSource): CardPaymentSource {
  return {
    id: source.id,
    type: "card",
    isPrimary: source.is_primary,
    brand: source.card.brand,
    last4: source.card.last4,
    expiry: {
      month: source.card.expiry_month,
      year: source.card.expiry_year
    },
    nameOnCard: source.card.first_name
  };
}

function fromBackendPayPalPaymentSource(source: BackendPayPalPaymentSource): PayPalPaymentSource {
  return {
    id: source.id,
    type: "paypal",
    isPrimary: source.is_primary,
    paypalEmail: source.paypal?.email
  };
}

export function getPaymentSourceTitle(paymentSource: PaymentSource): string {
  if (paymentSource.type === "card") {
    return _.startCase(paymentSource.brand) + " ending in " + paymentSource.last4;
  } else if (paymentSource.type === "paypal") {
    return paymentSource.paypalEmail ? `PayPal (${paymentSource.paypalEmail})` : "PayPal";
  }

  throw new Error("Unsupported payment source type");
}

export function toPaymentSourceRequest(formData: FormData): PaymentSourceRequest {
  const formDataObj = formDataToStructuredObject(formData);

  switch (formDataObj[PaymentSourceInputName.type]) {
    case "card":
      return {
        id: _.get(formDataObj, PaymentSourceInputName.id),
        type: "card",
        number: _.get(formDataObj, PaymentSourceInputName.card.number),
        cvv: _.get(formDataObj, PaymentSourceInputName.card.cvv),
        expiry: {
          year: _.get(formDataObj, PaymentSourceInputName.card.expiry.year),
          fullYear: _.get(formDataObj, PaymentSourceInputName.card.expiry.fullYear),
          month: _.get(formDataObj, PaymentSourceInputName.card.expiry.month)
        },
        name_on_card: _.get(formDataObj, PaymentSourceInputName.card.nameOnCard) || undefined
      };

    case "paypal":
      return {
        id: _.get(formDataObj, PaymentSourceInputName.id),
        type: "paypal",
        billing_agreement_token: _.get(formDataObj, PaymentSourceInputName.paypal.billingAgreementToken)
      };
  }

  throw new Error("Unsupported payment source type");
}
